import React, { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import PageHeader from '../components/PageHeader'

function highlight(text, q) {
  if (!text || !q) return text
  const idx = text.toLowerCase().indexOf(q.toLowerCase())
  if (idx === -1) return text
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ background:'var(--accent-bg)', color:'var(--accent)', borderRadius:2, padding:'0 2px' }}>{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </>
  )
}

export default function Search() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const q = (searchParams.get('q') || '').trim()

  const [results, setResults] = useState({ clients: [], projects: [], pipeline: [], marketing: [], development: [] })
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!q || q.length < 2) { setResults({ clients: [], projects: [], pipeline: [], marketing: [], development: [] }); return }
    setLoading(true)
    async function search() {
      const like = `%${q}%`
      const [cRes, bRes, pRes, mRes, dRes] = await Promise.all([
        supabase.from('clients').select('id,name,email,city,status').ilike('name', like),
        supabase.from('brands').select('id,name,category,client_id').ilike('name', like),
        supabase.from('pipeline').select('id,client_name,service_interest,stage,estimated_value').ilike('client_name', like),
        supabase.from('active_marketing').select('id,client_name,project_name,stage,payment_status').ilike('project_name', like),
        supabase.from('active_development').select('id,client_name,project_name,stage,payment_status').ilike('project_name', like),
      ])
      setResults({
        clients: cRes.data || [],
        projects: bRes.data || [],
        pipeline: pRes.data || [],
        marketing: mRes.data || [],
        development: dRes.data || [],
      })
      setLoading(false)
    }
    search()
  }, [q])

  const total = results.clients.length + results.projects.length + results.pipeline.length + results.marketing.length + results.development.length

  function fmt(n) { return '₹' + Number(n).toLocaleString('en-IN') }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <PageHeader
        title="Search"
        subtitle={q ? (loading ? 'Searching…' : `${total} result${total !== 1 ? 's' : ''} for "${q}"`) : 'Type a search term in the sidebar'}
      />
      <div style={{ padding: 28, flex: 1 }}>

        {q.length > 0 && q.length < 2 && (
          <div style={{ color: 'var(--text3)', fontSize: 13 }}>Enter at least 2 characters to search.</div>
        )}

        {!loading && q.length >= 2 && total === 0 && (
          <div style={{ color: 'var(--text3)', fontSize: 13 }}>No results found for "{q}".</div>
        )}

        {results.clients.length > 0 && (
          <Section title="Clients" count={results.clients.length}>
            {results.clients.map(c => (
              <ResultRow
                key={c.id}
                onClick={() => navigate('/clients')}
                badge="Client" badgeColor="badge-green"
                primary={highlight(c.name, q)}
                secondary={[c.city, c.email, c.status].filter(Boolean).join(' · ')}
              />
            ))}
          </Section>
        )}

        {results.projects.length > 0 && (
          <Section title="Projects (brands)" count={results.projects.length}>
            {results.projects.map(b => (
              <ResultRow
                key={b.id}
                onClick={() => navigate('/clients')}
                badge={b.category} badgeColor={b.category === 'Marketing' ? 'badge-blue' : 'badge-purple'}
                primary={highlight(b.name, q)}
                secondary="Under Clients tab"
              />
            ))}
          </Section>
        )}

        {results.marketing.length > 0 && (
          <Section title="Active Marketing" count={results.marketing.length}>
            {results.marketing.map(m => (
              <ResultRow
                key={m.id}
                onClick={() => navigate('/marketing')}
                badge="Marketing" badgeColor="badge-blue"
                primary={highlight(m.project_name, q)}
                secondary={`${m.client_name} · Stage: ${m.stage} · ${m.payment_status}`}
              />
            ))}
          </Section>
        )}

        {results.development.length > 0 && (
          <Section title="Active Development" count={results.development.length}>
            {results.development.map(d => (
              <ResultRow
                key={d.id}
                onClick={() => navigate('/development')}
                badge="Dev" badgeColor="badge-purple"
                primary={highlight(d.project_name, q)}
                secondary={`${d.client_name} · Stage: ${d.stage} · ${d.payment_status}`}
              />
            ))}
          </Section>
        )}

        {results.pipeline.length > 0 && (
          <Section title="Pipeline" count={results.pipeline.length}>
            {results.pipeline.map(p => (
              <ResultRow
                key={p.id}
                onClick={() => navigate('/pipeline')}
                badge={p.stage} badgeColor="badge-gray"
                primary={highlight(p.client_name, q)}
                secondary={`${p.service_interest || '—'} · ${fmt(p.estimated_value)}`}
              />
            ))}
          </Section>
        )}
      </div>
    </div>
  )
}

function Section({ title, count, children }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
        {title} <span style={{ fontWeight: 400 }}>({count})</span>
      </div>
      <div className="card" style={{ overflow: 'hidden' }}>
        {children}
      </div>
    </div>
  )
}

function ResultRow({ onClick, badge, badgeColor, primary, secondary }) {
  return (
    <div
      onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 16px', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
      onMouseLeave={e => e.currentTarget.style.background = ''}
    >
      <span className={`badge ${badgeColor}`} style={{ flexShrink: 0 }}>{badge}</span>
      <span style={{ fontWeight: 500, fontSize: 13, flex: 1 }}>{primary}</span>
      <span style={{ fontSize: 12, color: 'var(--text3)' }}>{secondary}</span>
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ color: 'var(--text3)', flexShrink: 0 }}>
        <path d="M4.5 2.5l4 3.5-4 3.5"/>
      </svg>
    </div>
  )
}
